import { AlertTriangle, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import classNames from 'classnames';
import { REEBELO_COUNTRY_CODE, ReebeloStoreT } from '@lambda/reebelo';
import { ToBeRemovedT } from '@/utils/hooks/cart/types';
import i18n from '@/settings/i18n';
import { priceFormater } from '@/settings/index';
import { localizeCondition } from '@/lib/collections/catalogue/helpers';
import { priceFormatSuperscript } from '@/lib/price';

const t = i18n.init('cart');

const flagFor = (vendor: string): string => {
  const store = vendor.split('#')[0];
  const code = REEBELO_COUNTRY_CODE[store as ReebeloStoreT];

  if (!code || code.length !== 2) {
    return '';
  }

  return code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
    .join('');
};

type PanelVariant = 'availability' | 'price';

const heading = (variant: PanelVariant, lines: ToBeRemovedT[]) => {
  const count = lines.length;

  if (variant === 'availability') {
    const allRemoved = lines.every((l) => l.stock === 0);

    if (allRemoved) {
      return count === 1
        ? t`${{
            count,
          }} item was removed from your cart due to an availability change.`
        : t`${{
            count,
          }} items were removed from your cart due to an availability change.`;
    }

    return count === 1
      ? t`${{
          count,
        }} item in your cart was updated due to an availability change.`
      : t`${{
          count,
        }} items in your cart were updated due to an availability change.`;
  }

  return count === 1
    ? t`${{
        count,
      }} item in your cart was updated due to a price change.`
    : t`${{
        count,
      }} items in your cart were updated due to a price change.`;
};

const UpdatePanel = ({
  variant,
  lines,
  onDismiss,
  onSimilarOffers,
  className,
}: {
  variant: PanelVariant;
  lines: ToBeRemovedT[];
  onDismiss: (typeId: string) => void;
  onSimilarOffers: (typeId: string) => void;
  className: string;
}) => {
  if (lines.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames(
        'mb-6 mt-2 overflow-hidden rounded-2xl border border-[#F2F2F2] bg-[#F8F8F8] p-4 text-sm shadow-[0_16px_5px_0_rgba(0,_0,_0,_0.00),_0_10px_4px_0_rgba(0,_0,_0,_0.00),_0_6px_4px_0_rgba(0,_0,_0,_0.01),_0_3px_3px_0_rgba(0,_0,_0,_0.02),_0_1px_1px_0_rgba(0,_0,_0,_0.02)] lg:bg-white lg:p-0',
        className,
      )}
    >
      <div className="flex items-center gap-2 lg:px-6 lg:py-4">
        <AlertTriangle className="shrink-0 text-gray-700" strokeWidth={2.5} />
        <h2 className="text-base font-bold leading-tight">
          {heading(variant, lines)}
        </h2>
      </div>
      <div className="mt-4 divide-y rounded-xl border-t bg-white lg:mt-0 lg:rounded-none">
        {lines.map(
          ({
            line,
            stock,
            priceChanged,
            oldPrice,
            newPrice,
            vendorChanged,
            newVendor,
          }) => {
            const [title] = line.title.split(' - ');

            const specs = (line?.variants ?? [])
              ?.map((v) => {
                if (!v.value) {
                  return null;
                }

                if (title.includes(v.value)) {
                  return null;
                }

                if (v.name === 'Battery Health') {
                  const isNumeric = /^\d+$/.test(v.value);

                  if (!isNumeric) {
                    return v.value;
                  }

                  if (v.value === '100') {
                    return `${v.value}%`;
                  }

                  return t`Min. ${{ value: v.value }}%`;
                }

                if (v.name === 'Condition') {
                  return localizeCondition(v.value);
                }

                return v.value;
              })
              .filter(Boolean);

            const productId =
              line?.notes?.find((note) => note.name === '_product_id')?.value ??
              '';
            const skuId =
              line?.notes?.find((note) => note.name === '_sku_id')?.value ?? '';
            const isOutOfStock = stock === 0;

            return (
              <div className="flex flex-col justify-between gap-4 p-4 sm:flex-row sm:items-center lg:px-6">
                <div className="flex items-center gap-x-2">
                  <Image
                    width={100}
                    height={100}
                    alt={line.title}
                    src={line?.image ?? ''}
                    className="h-[40px] w-[40px] shrink-0 object-contain"
                  />
                  <div>
                    <div className="font-bold">{title}</div>
                    <div
                      // eslint-disable-next-line tailwindcss/no-custom-classname
                      className="two-line-ellipsis text-xs text-[#767676]"
                    >
                      {specs.join(' - ')}
                    </div>
                    {!isOutOfStock && vendorChanged && newVendor && (
                      <div className="mt-1 text-xs text-[#767676]">
                        {t`Professionally refurbished & shipped by`}{' '}
                        <span className="underline">
                          {newVendor.split('#')[1] ?? newVendor}
                        </span>
                        {flagFor(newVendor) && (
                          <span className="ml-1">{flagFor(newVendor)}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                    {!isOutOfStock &&
                      priceChanged &&
                      oldPrice != null &&
                      newPrice != null && (
                        <div className="flex items-center justify-center gap-2 text-xs text-[#767676]">
                          <span className="line-through" translate="no">
                            {priceFormatSuperscript(
                              priceFormater.long(oldPrice / 100),
                            )}
                          </span>
                          <ArrowRight className="h-4 w-4 shrink-0" />{' '}
                          <span
                            className="font-semibold text-[#1F2323]"
                            translate="no"
                          >
                            {priceFormatSuperscript(
                              priceFormater.long(newPrice / 100),
                            )}
                          </span>
                        </div>
                      )}
                    {!isOutOfStock && line.quantity > stock && (
                      <div className="flex w-1/2 items-center justify-center gap-2 text-xs text-[#767676] sm:w-[150px]">
                        <span className="inline-block">{t`Quantity:`}</span>{' '}
                        {line.quantity}{' '}
                        <ArrowRight className="h-4 w-4 shrink-0" /> {stock}
                      </div>
                    )}
                    {isOutOfStock && productId && (
                      <Link
                        href={`collections/${encodeURIComponent(productId)}${
                          skuId ? `?skuId=${encodeURIComponent(skuId)}` : ''
                        }`}
                        onClick={() => onSimilarOffers(line.typeId)}
                        className="flex h-10 w-full items-center justify-center whitespace-nowrap rounded bg-[#00B67A] px-4 text-xs text-white hover:bg-[#00B67A]/90 sm:w-fit"
                      >
                        {t`View Similar Offers`}
                      </Link>
                    )}
                    <button
                      className="h-10 w-full whitespace-nowrap rounded bg-[#F2F2F2] px-4 text-xs text-[#1F2323]/50 hover:bg-[#eeeeee] sm:w-fit"
                      onClick={() => onDismiss(line.typeId)}
                    >
                      {t`Dismiss`}
                    </button>
                  </div>
                </div>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

const OutOfStockPanel = ({
  removedCartLines,
  hasOutOfStockItems,
  onDismiss,
  onSimilarOffers,
  className,
}: {
  removedCartLines: ToBeRemovedT[];
  hasOutOfStockItems: boolean;
  onDismiss: (typeId: string) => void;
  onSimilarOffers: (typeId: string) => void;
  className: string;
}) => {
  if (!hasOutOfStockItems) {
    return null;
  }

  const availabilityLines = removedCartLines.filter(
    (l) => l.stock === 0 || l.line.quantity > l.stock,
  );
  const priceLines = removedCartLines.filter(
    (l) => l.stock !== 0 && l.line.quantity <= l.stock,
  );

  return (
    <>
      <UpdatePanel
        variant="availability"
        lines={availabilityLines}
        onDismiss={onDismiss}
        onSimilarOffers={onSimilarOffers}
        className={className}
      />
      <UpdatePanel
        variant="price"
        lines={priceLines}
        onDismiss={onDismiss}
        onSimilarOffers={onSimilarOffers}
        className={className}
      />
    </>
  );
};

export default OutOfStockPanel;
